import { Howl } from "howler";

// Função para gerar som de vitória proceduralmente
function generateVictorySound(): string {
  if (typeof window === "undefined") return "";
  
  const sampleRate = 44100;
  const duration = 2.0;
  const totalSamples = Math.floor(sampleRate * duration);
  const buffer = new Float32Array(totalSamples);
  
  // Acordes de vitória: C major -> G major -> C major (mais alto)
  const notes = [
    { freq: 523.25, start: 0, duration: 0.3 },    // C5
    { freq: 659.25, start: 0.1, duration: 0.3 },  // E5
    { freq: 783.99, start: 0.2, duration: 0.3 },  // G5
    { freq: 1046.50, start: 0.4, duration: 0.8 }, // C6 (sustain)
  ];
  
  for (const note of notes) {
    const startSample = Math.floor(note.start * sampleRate);
    const endSample = Math.floor((note.start + note.duration) * sampleRate);
    
    for (let i = startSample; i < endSample && i < totalSamples; i++) {
      const t = (i - startSample) / sampleRate;
      const envelope = Math.sin(Math.PI * t / note.duration); // Fade in/out
      const sample = Math.sin(2 * Math.PI * note.freq * t) * envelope * 0.25;
      buffer[i] += sample;
    }
  }
  
  // Adicionar brilho (harmônicos)
  for (let i = 0; i < totalSamples; i++) {
    const t = i / sampleRate;
    if (t > 0.4 && t < 1.2) {
      const envelope = Math.sin(Math.PI * (t - 0.4) / 0.8) * 0.15;
      buffer[i] += Math.sin(2 * Math.PI * 2093 * t) * envelope; // C7
    }
  }
  
  // Normalizar
  let peak = 0;
  for (let i = 0; i < buffer.length; i++) {
    peak = Math.max(peak, Math.abs(buffer[i]));
  }
  if (peak > 0) {
    for (let i = 0; i < buffer.length; i++) {
      buffer[i] = buffer[i] / peak * 0.9;
    }
  }
  
  return encodeWavToDataUrl(buffer, sampleRate);
}

// Função para gerar som de derrota
function generateDefeatSound(): string {
  if (typeof window === "undefined") return "";
  
  const sampleRate = 44100;
  const duration = 1.5;
  const totalSamples = Math.floor(sampleRate * duration);
  const buffer = new Float32Array(totalSamples);
  
  // Notas descendentes tristes
  const notes = [
    { freq: 440, start: 0, duration: 0.4 },      // A4
    { freq: 392, start: 0.3, duration: 0.4 },    // G4
    { freq: 349.23, start: 0.6, duration: 0.4 }, // F4
    { freq: 293.66, start: 0.9, duration: 0.6 }, // D4
  ];
  
  for (const note of notes) {
    const startSample = Math.floor(note.start * sampleRate);
    const endSample = Math.floor((note.start + note.duration) * sampleRate);
    
    for (let i = startSample; i < endSample && i < totalSamples; i++) {
      const t = (i - startSample) / sampleRate;
      const envelope = Math.exp(-t * 3) * 0.3; // Decay exponencial
      const sample = Math.sin(2 * Math.PI * note.freq * t) * envelope;
      buffer[i] += sample;
      
      // Adicionar sub-harmônico para tristeza
      buffer[i] += Math.sin(2 * Math.PI * note.freq * 0.5 * t) * envelope * 0.5;
    }
  }
  
  // Normalizar
  let peak = 0;
  for (let i = 0; i < buffer.length; i++) {
    peak = Math.max(peak, Math.abs(buffer[i]));
  }
  if (peak > 0) {
    for (let i = 0; i < buffer.length; i++) {
      buffer[i] = buffer[i] / peak * 0.8;
    }
  }
  
  return encodeWavToDataUrl(buffer, sampleRate);
}

function encodeWavToDataUrl(signal: Float32Array, sampleRate: number): string {
  const buffer = new ArrayBuffer(44 + signal.length * 2);
  const view = new DataView(buffer);
  
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  
  writeString(0, "RIFF");
  view.setUint32(4, 36 + signal.length * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, signal.length * 2, true);
  
  let offset = 44;
  for (let i = 0; i < signal.length; i++) {
    const s = Math.max(-1, Math.min(1, signal[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  
  const blob = new Blob([buffer], { type: "audio/wav" });
  return URL.createObjectURL(blob);
}

// Cache dos sons
let victorySound: Howl | null = null;
let defeatSound: Howl | null = null;
let victoryUrl: string | null = null;
let defeatUrl: string | null = null;

export function getVictorySound(): Howl {
  if (!victorySound && typeof window !== "undefined") {
    victoryUrl = generateVictorySound();
    victorySound = new Howl({
      src: [victoryUrl],
      volume: 0.7,
      onload: () => {
        // Revogar URL após carregar para liberar memória
        if (victoryUrl) {
          URL.revokeObjectURL(victoryUrl);
          victoryUrl = null;
        }
      }
    });
  }
  return victorySound!;
}

export function getDefeatSound(): Howl {
  if (!defeatSound && typeof window !== "undefined") {
    defeatUrl = generateDefeatSound();
    defeatSound = new Howl({
      src: [defeatUrl],
      volume: 0.6,
      onload: () => {
        // Revogar URL após carregar para liberar memória
        if (defeatUrl) {
          URL.revokeObjectURL(defeatUrl);
          defeatUrl = null;
        }
      }
    });
  }
  return defeatSound!;
}

export function playVictorySound(volume?: number) {
  const sound = getVictorySound();
  if (sound) {
    if (volume !== undefined) {
      sound.volume(volume);
    }
    sound.play();
  }
}

export function playDefeatSound(volume?: number) {
  const sound = getDefeatSound();
  if (sound) {
    if (volume !== undefined) {
      sound.volume(volume);
    }
    sound.play();
  }
}
